/**
 * useTutorLiveChromeHelp — « Un problème ? » du masteur (`<LiveHelpSheet viewer="tutor">`) : choisir son
 * problème, ce que l'écran a vérifié, puis « Signaler un problème ».
 *
 * L'ancien parcours du tuteur (`TroubleshootingPanel role="tutor"` puis `LiveHelpModal`), mêmes appels
 * et mêmes mesures : `troubleshooting.panel.opened` à chaque ouverture (ou changement du problème
 * initial), `troubleshooting.problem.selected` au choix d'un problème, `bug_report.modal.opened` à
 * l'ouverture du signalement, `bug_report.submitted` à son envoi (`POST api/v2/live-sessions/{id}/issue-reports`,
 * `category`, `description`, `diagnostics.reconnections`, `platform`, `attachment_files`). Les pièces
 * jointes passent par le même chemin que le signalement de l'élève (`issueReportAttachments`).
 *
 * Sous les étapes d'un diagnostic, ses gestes (`tutorHelpActions`, le web) : « J'ai autorisé le micro / la
 * caméra » relance la demande au navigateur pour cet appareil (`PermissionAction`, `confirmMediaPermission`) —
 * autorisé, le diagnostic le relit aussitôt ; encore refusé, la ligne devient « Rafraîchir la page » avec
 * « Permission non détectée… » — et « Changer de micro / de caméra » (`DeviceChangeButton`) ouvre la fenêtre
 * des appareils sur son onglet (`onOpenDevices`). Aucune mesure : l'ancien n'en avait pas.
 *
 * « Toujours un problème ? » n'offre au tuteur que rafraîchir (web), ouvrir les paramètres (natif) et
 * signaler : ni main ni chat. Le signalement parti, la feuille se ferme et l'accusé « Signalement envoyé,
 * merci ! » passe à l'écran (`onReported`).
 */
import { useEffect, useRef, useState } from "react"
import { Linking, Platform } from "react-native"

import { type LiveHelpSheetProps } from "../../../../../../design-system/templates/LiveHelpSheet/LiveHelpSheet"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import { type EngineTelemetry } from "../../../../../../engine/shared/telemetry"
import { useMediaPickerAdapter } from "../../../hooks/useMediaPickerAdapter"
import { type UILiveProblem, type UILiveReportCategory } from "../../../types/studentLive"
import {
    tutorHelpActions,
    tutorHelpDiagnostic,
    tutorReportReconnections,
    type TutorHelpActionId,
    type TutorHelpContext,
} from "../../../../view-models/tutor/Live/tutorLiveChromeViewModel"
import {
    ISSUE_REPORT_MAX_ATTACHMENTS,
    ReportAbandonedError,
    admitPickedAttachments,
    issueReportFailureMessage,
    uploadIssueAttachments,
} from "../../student/Live/issueReportAttachments"
import {
    TUTOR_LIVE_EVENTS,
    confirmMediaPermission,
    type MediaDevicesLike,
    type TutorLiveChromeProtocol,
} from "./useTutorLiveChromeProtocol"

/** Le `formId` des pièces du signalement — celui de l'ancienne modale. */
const ISSUE_REPORT_FORM_ID = "issue_report"
const ATTACH_LABEL = "Joindre une capture ou une vidéo"
const ATTACH_HEIGHT = 72

type PickedMedia = { uri: string; width?: number; height?: number; mimeType?: string; name?: string; fileSize?: number }

export type UseTutorLiveChromeHelpArgs = {
    /** La feuille ouverte, et le problème sur lequel elle s'ouvre (`null` : le choix). */
    request: { problem: UILiveProblem | null } | null
    onClose: () => void
    context: TutorHelpContext
    liveSessionId: number | null
    logs: ({ action?: string | null } | null)[] | null | undefined
    protocol: TutorLiveChromeProtocol
    track: (event: { name: string }) => void
    telemetry: EngineTelemetry
    onReported: () => void
    /**
     * « Changer de micro / de caméra » : la fenêtre des appareils sur cet onglet. Absent (pas de fenêtre des
     * appareils) : pas de ligne.
     */
    onOpenDevices?: (tab: "microphone" | "camera") => void
    /** Le navigateur que « J'ai autorisé… » relance. Défaut : `navigator.mediaDevices`, lu au geste. */
    mediaDevices?: MediaDevicesLike
}

type PerDevice = { mic: boolean; camera: boolean }
const NONE: PerDevice = { mic: false, camera: false }

const browserMediaDevices = (): MediaDevicesLike | undefined =>
    typeof navigator !== "undefined"
        ? ((navigator as unknown as { mediaDevices?: MediaDevicesLike }).mediaDevices ?? undefined)
        : undefined

export function useTutorLiveChromeHelp(args: UseTutorLiveChromeHelpArgs): Omit<LiveHelpSheetProps, "viewer"> | null {
    const { request, onClose, context, liveSessionId, logs, protocol, track, telemetry } = args
    const visible = request != null
    const [step, setStep] = useState<"choose" | "diagnostic" | "report">("choose")
    const [problem, setProblem] = useState<UILiveProblem | null>(null)
    const [category, setCategory] = useState<UILiveReportCategory | null>(null)
    const [description, setDescription] = useState("")
    const [images, setImages] = useState<PickedMedia[]>([])
    const [sending, setSending] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const sendToken = useRef(0)
    // « J'ai autorisé… » : l'appareil que la demande relancée a obtenu (le panneau le relit aussitôt, comme
    // `usePermissions.requestPermissions`), et celui qu'elle n'a pas obtenu (`PermissionAction.denied`).
    const [granted, setGranted] = useState<PerDevice>(NONE)
    const [stillDenied, setStillDenied] = useState<PerDevice>(NONE)
    const asking = useRef(false)

    const picker = useMediaPickerAdapter({
        images,
        setImages: (next: PickedMedia[]) => {
            const { kept, refusal } = admitPickedAttachments(images, next)
            setImages(kept)
            setError(refusal)
        },
        formId: ISSUE_REPORT_FORM_ID,
        multiple: true,
        selectionLimit: ISSUE_REPORT_MAX_ATTACHMENTS - images.length,
        enableVideo: true,
        enableCamera: false,
        enableExternalLink: false,
        enableDocument: IS_WEB,
        buttonLabel: ATTACH_LABEL,
        addMoreButtonLabel: ATTACH_LABEL,
        showAddMoreButton: images.length < ISSUE_REPORT_MAX_ATTACHMENTS,
    })

    const initialProblem = request?.problem ?? null
    // Chaque ouverture, ou un autre problème initial, se mesure et repart de lui (`TroubleshootingPanel.js:1086-1092`).
    useEffect(() => {
        if (!visible) return
        setProblem(initialProblem)
        setStep(initialProblem != null ? "diagnostic" : "choose")
        setGranted(NONE)
        track(TUTOR_LIVE_EVENTS.panelOpened)
    }, [visible, initialProblem, track])

    // Le refus d'une demande relancée vit avec son diagnostic (`PermissionAction` se remontait avec lui).
    useEffect(() => setStillDenied(NONE), [visible, problem])

    const reset = () => {
        sendToken.current += 1
        setSending(false)
        setCategory(null)
        setDescription("")
        setImages([])
        setError(null)
        setStep("choose")
    }

    const send = () => {
        if (liveSessionId == null || category == null || sending) return
        setSending(true)
        setError(null)
        const token = ++sendToken.current
        const current = () => token === sendToken.current
        uploadIssueAttachments(images, undefined, current)
            .then((keys) => {
                if (!current()) throw new ReportAbandonedError()
                return protocol.submitIssueReport(liveSessionId, {
                    category,
                    description,
                    diagnostics: { reconnections: tutorReportReconnections(logs) },
                    platform: Platform.OS,
                    ...(keys.length > 0 ? { attachment_files: keys } : {}),
                })
            })
            .then(() => {
                if (!current()) return
                reset()
                onClose()
                args.onReported()
            })
            .catch((cause: unknown) => {
                if (cause instanceof ReportAbandonedError) return
                telemetry.error("issue_report", cause)
                if (current()) setError(issueReportFailureMessage(cause))
            })
            .finally(() => {
                if (current()) setSending(false)
            })
    }

    const helpContext: TutorHelpContext = {
        ...context,
        micAllowed: granted.mic ? true : context.micAllowed,
        cameraAllowed: granted.camera ? true : context.cameraAllowed,
    }

    const confirm = (device: keyof PerDevice) => {
        if (asking.current) return
        asking.current = true
        confirmMediaPermission(device === "mic" ? "microphone" : "camera", args.mediaDevices ?? browserMediaDevices())
            .then((allowed) => {
                if (allowed) setGranted((current) => ({ ...current, [device]: true }))
                else setStillDenied((current) => ({ ...current, [device]: true }))
            })
            .finally(() => {
                asking.current = false
            })
    }

    const runAction = (id: TutorHelpActionId) => {
        switch (id) {
            case "allow_mic":
                return confirm("mic")
            case "allow_camera":
                return confirm("camera")
            case "refresh_after_denial":
                return window.location.reload()
            case "change_mic":
                return args.onOpenDevices?.("microphone")
            case "change_camera":
                return args.onOpenDevices?.("camera")
        }
    }

    if (!visible) return null
    const helpActions = tutorHelpActions(problem, helpContext, {
        stillDenied,
        canChangeDevices: args.onOpenDevices != null,
    })
    const actions = helpActions.map(({ id, ...action }) => ({ ...action, onPress: () => runAction(id) }))
    // Après un refus répété, « Rafraîchir la page » est déjà sous « À essayer », avec sa raison : la
    // feuille ne le redit pas sous « Toujours un problème ? ».
    const refreshListed = helpActions.some((action) => action.id === "refresh_after_denial")
    const diagnostic = tutorHelpDiagnostic(problem, helpContext)
    return {
        visible,
        onClose: () => {
            reset()
            onClose()
        },
        step,
        problem,
        onSelectProblem: (selected) => {
            track(TUTOR_LIVE_EVENTS.problemSelected)
            setProblem(selected)
            setStep("diagnostic")
        },
        onBack: () => setStep(step === "report" ? (problem != null ? "diagnostic" : "choose") : "choose"),
        diagnostic: actions.length > 0 ? { ...diagnostic, actions } : diagnostic,
        onRefresh: IS_WEB && !refreshListed ? () => window.location.reload() : undefined,
        onOpenSettings: IS_WEB ? undefined : () => void Linking.openSettings(),
        onReport: () => {
            track(TUTOR_LIVE_EVENTS.reportOpened)
            setStep("report")
        },
        report: {
            category,
            onSelectCategory: setCategory,
            description,
            onChangeDescription: setDescription,
            onSend: send,
            sending,
            media: { ...picker, minHeight: ATTACH_HEIGHT },
            error,
        },
    }
}
